import classnames from 'classnames';

import { type CSSProperties, useEffect, useRef } from 'react';
import type { EmbedElementProps } from './Embed.types';

export function EmbedElement(props: EmbedElementProps) {
  const { id, element, attributes, className } = props;
  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    backgroundColor,
    borderWidth,
    borderRadius,
    borderStyle,
    borderColor,
    width,
    maxWidth,
    html,
    htmlOriginal,
  } = element.attrs || {};
  const ref = useRef<HTMLDivElement>(null);
  const noIframe = htmlOriginal?.match(/<!--\s*beehiiv:no-iframe\s*-->/);
  const htmlToUse = (noIframe ? htmlOriginal : html) || '';

  useEffect(() => {
    if (ref.current) {
      const isInEditor = ref.current.closest('[data-node-view-wrapper]') !== null;
      if (isInEditor) {
        if (noIframe && ref.current.innerText === '') {
          ref.current.innerHTML = `<div class="border-2 border-red-300 border-dotted text-center bg-white text-black p-4">Your HTML embed appears to be <code class="font-bold">&lt;script&gt;</code> only.
          Scripts are not executed in the editor, but will render when published. You can also Preview to verify your HTML embed.</div>`;
        }
        return;
      }
      if (noIframe) {
        // we only want to render HTML embeds with scripts once
        // so we check if the element has a data-rendered attribute
        if (!ref.current.dataset.rendered) {
          ref.current.innerHTML = htmlToUse;
          remountScripts(id);
          ref.current.dataset.rendered = 'true';
        }
      }
    }
  }, [id, htmlToUse, noIframe]);

  // no-iframe embeds will NOT be server-rendered. We also don't use
  // dangerouslySetInnerHTML for them, so if React re-renders the component,
  // it won't overwrite the HTML embed content. We manually set the innerHTML
  // in the useEffect above, and set a flag `data-rendered` to true to signify
  // that the embed has been rendered, so we don't overwrite it again.

  // For regular HTML embeds, we use dangerouslySetInnerHTML to render the HTML
  // and also allow for server-side rendering. This is because it's either plain HTML
  // or any scripts that would modify the DOM has been wrapped in an <iframe>

  const htmlProp = noIframe ? undefined : { __html: html ?? '' };
  return (
    <div
      id={id}
      ref={ref}
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        customAttributes?.className,
      )}
      style={
        {
          position: 'relative',
          padding: padding,
          backgroundColor: backgroundColor,
          borderWidth: borderWidth,
          borderRadius: borderRadius,
          borderStyle: borderStyle,
          borderColor: borderColor,
          width: width,
          maxWidth: maxWidth,
          whiteSpace: 'normal',
        } as CSSProperties
      }
      {...attributes}
      dangerouslySetInnerHTML={htmlProp}
    />
  );
}

// This function remounts any <script> tags are part of the HTML embed.
// The browser will not execute <script> tags that are added via innerHTML,
// on the client. And we can't SSR them either since this causes hydration errors.
const remountScripts = (id: string) => {
  const embed = document.getElementById(id);
  if (!embed) {
    return;
  }
  const scripts = Array.from(embed.querySelectorAll('script')) as HTMLScriptElement[];
  let i = 0;
  for (const script of scripts) {
    const attributes = Array.from(script.attributes);

    // create a new script tag thats a copy of the old one
    const newScript = document.createElement('script');
    attributes.forEach((attribute) => {
      newScript.setAttribute(attribute.name, attribute.value);
    });
    newScript.innerHTML = script.innerHTML;
    newScript.id = `html-embed-script-${id}-${i++}`;
    const existingScript = document.getElementById(newScript.id);
    if (existingScript) {
      // remove the existing script to avoid duplicates
      existingScript.remove();
    }
    embed.appendChild(newScript);
  }
};
